import { Tables } from '@/lib/supabase/supabase_types_override';
import { supabase } from '@/lib/supabase/supabaseClient';
import { getUserSession } from '@/lib/supabase/queries';
import { GetProjectsListProps } from '.';
import { sortInProjectRoleOrder } from '@/features/rbac/lib/sortByRole';
import { getOrganizationById } from '@/features/organization/hooks/useQuery/useGetOrganizationById';
import { DEFAULT_CRM_SYSTEM } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen';

export const getUserOrganizationsList = async (userId: Tables<'user'>['id']) =>
  await supabase
    .from('organisation_members')
    .select('organisation_id')
    .eq('user_id', userId);

export const getProjectsListByOrgId = async ({
  orgId,
  sortByCreatedAtAscending,
}: GetProjectsListProps) => {
  const orgData = await getOrganizationById(orgId);
  const { data: projectsData, error: projectsError } = await supabase
    .from('project')
    .select(
      `
            *,
            project_members(user_id, user(id, first_name, last_name, email, signed_up)),
            crm_project(
                *,
                crm(
                    *,
                    crm_config(*)
                )
            ),
            manager:user!project_manager_id_fkey(
                first_name,
                last_name
            ),
            organisation(
                organisation_members(*),
                name
            ),
            crm(*),
            survey(*,crm_config(*))
        `
    )
    .eq('organisation_id', orgId)
    .in('crm_system', orgData.crm_connections ?? [DEFAULT_CRM_SYSTEM])
    .order('created_at', { ascending: sortByCreatedAtAscending });

  if (projectsError) throw projectsError;

  // For each project, fetch role information for its members
  const result = await Promise.all(
    projectsData?.map(async (project) => {
      const memberRows = project.project_members || [];
      const userIds = memberRows.map((m) => m.user_id);

      const { data: rolesData, error: rolesError } = await supabase
        .from('user_project_role')
        .select(
          'user_id, project_role_id, project_id, role:role_based_project_access_control(*)'
        )
        .in('user_id', userIds)
        .eq('project_id', project.id);

      if (rolesError) throw rolesError;

      const membersWithRoles = memberRows
        .map((m) => ({
          ...m,
          user: m.user,
          role: rolesData.find((r) => r.user_id === m.user_id)?.role || null,
        }))
        .sort((a, b) => sortInProjectRoleOrder(a.role?.role, b.role?.role))
      return {
        ...project,
        project_members: membersWithRoles,
      };
    }) || []
  );

  return result;
};

export const getProjectsListNotInUserOrganizations = async (
  sortByCreatedAtAscending: boolean = false
) => {
  // Get current user session
  const {
    data: { session },
    error: userError,
  } = await getUserSession();
  if (userError) throw userError;

  const {
    user: { id: userId },
  } = session!;

  // Get organizations the user belongs to
  const { data: organizationsData, error: organizationIdsError } =
    await getUserOrganizationsList(userId);
  if (organizationIdsError) throw organizationIdsError;

  const organizationIdsRaw = organizationsData!.map(
    ({ organisation_id }) => organisation_id
  );

  // Fetch projects NOT part of any organization the user belongs to
  const { data: projectsData, error: projectsError } = await supabase
    .from('project')
    .select(
      `
            *,
            project_members(user_id, user(id, first_name, last_name, email, signed_up)),
            crm_project(
                *,
                crm(
                    *,
                    crm_config(*)
                )
            ),
            manager:user!project_manager_id_fkey(
                first_name,
                last_name
            ),
            organisation(
                organisation_members(*),
                name
            ),
            crm(*),
            survey(*,crm_config(*))
        `
    )
    .not('organisation_id', 'in', `(${organizationIdsRaw.join(',')})`)
    .order('created_at', { ascending: sortByCreatedAtAscending });

  if (projectsError) throw projectsError;

  // For each project, fetch role information for its members
  const result = await Promise.all(
    projectsData?.map(async (project) => {
      const memberRows = project.project_members || [];
      const userIds = memberRows.map((m) => m.user_id);

      const { data: rolesData, error: rolesError } = await supabase
        .from('user_project_role')
        .select(
          'user_id, project_role_id, project_id, role:role_based_project_access_control(*)'
        )
        .in('user_id', userIds)
        .eq('project_id', project.id);

      if (rolesError) throw rolesError;

      const membersWithRoles = memberRows
        .map((m) => ({
          ...m,
          user: m.user,
          role: rolesData.find((r) => r.user_id === m.user_id)?.role || null,
        }))
        .sort((a, b) => sortInProjectRoleOrder(a.role?.role, b.role?.role));
      return {
        ...project,
        project_members: membersWithRoles,
      };
    }) || []
  );

  return result;
};

export const getProjectsList = ({
  orgId,
  sortByCreatedAtAscending,
}: GetProjectsListProps) =>
  Number.isInteger(orgId)
    ? getProjectsListByOrgId({ orgId, sortByCreatedAtAscending })
    : getProjectsListNotInUserOrganizations(sortByCreatedAtAscending);
